export type ShiftKey = 'MANHA' | 'TARDE' | 'NOITE';
export type SimEventKind = 'rfid' | 'rfid_saida' | 'chamada' | 'recreio' | 'comunicado';
export type SimEventStatus = 'pending' | 'running' | 'done' | 'skipped';
export type QueueControl = 'auto' | 'pause_next' | 'manual';
export type SimEngineMode = 'COCKPIT' | 'CHRONOS';
export type DiaSemanaKey = 'SEGUNDA' | 'TERCA' | 'QUARTA' | 'QUINTA' | 'SEXTA' | 'SABADO';

/** Ordem letiva do dia — nunca reinicia o mesmo turno ao fechar a janela. */
export const SHIFT_ORDER: ShiftKey[] = ['MANHA', 'TARDE', 'NOITE'];

/** Próximo turno disponível após o atual, ou null se acabou o dia. */
export function nextAvailableShift(
  current: ShiftKey,
  available: ShiftKey[],
): ShiftKey | null {
  const idx = SHIFT_ORDER.indexOf(current);
  if (idx < 0) return null;
  const set = new Set(available.length ? available : SHIFT_ORDER);
  for (let i = idx + 1; i < SHIFT_ORDER.length; i++) {
    if (set.has(SHIFT_ORDER[i])) return SHIFT_ORDER[i];
  }
  return null;
}

/** Primeiro turno do dia (respeita turnos cadastrados na escola). */
export function firstAvailableShift(available: ShiftKey[]): ShiftKey {
  const set = new Set(available.length ? available : SHIFT_ORDER);
  for (const s of SHIFT_ORDER) {
    if (set.has(s)) return s;
  }
  return 'MANHA';
}

/**
 * Próximo dia letivo a partir de `fromYmd` (+1).
 * Sábado → Segunda (+2); Domingo → Segunda (+1).
 */
export function nextSchoolDay(fromYmd: string): { ymd: string; diaSemana: DiaSemanaKey } {
  const d = parseYmd(fromYmd);
  d.setDate(d.getDate() + 1);
  const js = d.getDay();
  if (js === 6) d.setDate(d.getDate() + 2);
  else if (js === 0) d.setDate(d.getDate() + 1);
  const ymd = formatYmd(d);
  return { ymd, diaSemana: ymdToDiaSemana(ymd) };
}

export type SimEvent = {
  id: string;
  at: string;
  end?: string;
  kind: SimEventKind;
  label: string;
  status: SimEventStatus;
  turmaId?: string;
  professorId?: string;
  materiaId?: string;
  /** Slot da grade — permite casar mapa Central Chamadas. */
  horarioAulaId?: string;
  /** Número da aula no turno (para POST chamada). */
  numeroAula?: number;
  professorNome?: string;
  turmaNome?: string;
};

export type ShiftDayPlan = {
  success: boolean;
  error?: string;
  turnoId?: string;
  turnoNome?: string;
  diaSemana?: string;
  diaForaDoTurno?: boolean;
  diasFuncionamento?: string[];
  boundaries?: {
    abertura: string;
    entrada: string;
    saida: string;
    fechamento: string;
  };
  slots?: Array<{
    at: string;
    end: string;
    tipo: 'AULA' | 'INTERVALO' | 'PORTAO' | 'SAIDA';
    label: string;
    numeroAula: number | null;
    chamadas?: Array<{
      turmaId: string;
      turmaNome: string;
      professorId: string | null;
      professorNome?: string | null;
      materiaId: string | null;
      materiaNome?: string | null;
      horarioAulaId?: string | null;
    }>;
    sintetico?: boolean;
  }>;
  roster?: {
    turmas: number;
    alunos: number;
    alunosComTag: number;
    professores: number;
    responsaveis: number;
    alunosComTagIds?: Array<{
      id: string;
      nome: string;
      tagId?: string;
      tagCodigo?: string;
      turmaId?: string | null;
    }>;
    professorIds?: string[];
  };
};

export type ShiftWindow = {
  key: ShiftKey;
  label: string;
  startHm: string;
  endHm: string;
  events: Array<{ at: string; kind: SimEventKind; label: string }>;
};

export const SHIFT_WINDOWS: Record<ShiftKey, ShiftWindow> = {
  MANHA: {
    key: 'MANHA',
    label: 'Manhã',
    startHm: '06:40',
    endHm: '12:30',
    events: [
      { at: '07:00', kind: 'rfid', label: 'Entrada RFID' },
      { at: '07:30', kind: 'chamada', label: 'Chamada' },
      { at: '08:30', kind: 'comunicado', label: 'Comunicado' },
    ],
  },
  TARDE: {
    key: 'TARDE',
    label: 'Tarde',
    startHm: '12:40',
    endHm: '18:30',
    events: [
      { at: '13:00', kind: 'rfid', label: 'Entrada RFID' },
      { at: '13:30', kind: 'chamada', label: 'Chamada' },
      { at: '14:30', kind: 'comunicado', label: 'Comunicado' },
    ],
  },
  NOITE: {
    key: 'NOITE',
    label: 'Noite',
    startHm: '18:10',
    endHm: '22:30',
    events: [
      { at: '18:30', kind: 'rfid', label: 'Entrada RFID' },
      { at: '19:00', kind: 'chamada', label: 'Chamada' },
      { at: '20:00', kind: 'comunicado', label: 'Comunicado' },
    ],
  },
};

export const DIAS_UTEIS: DiaSemanaKey[] = [
  'SEGUNDA',
  'TERCA',
  'QUARTA',
  'QUINTA',
  'SEXTA',
];

export const DIA_LABEL: Record<DiaSemanaKey, string> = {
  SEGUNDA: 'SEG',
  TERCA: 'TER',
  QUARTA: 'QUA',
  QUINTA: 'QUI',
  SEXTA: 'SEX',
  SABADO: 'SAB',
};

const JS_TO_DIA: DiaSemanaKey[] = [
  'SEGUNDA', // placeholder — Sunday handled separately
  'SEGUNDA',
  'TERCA',
  'QUARTA',
  'QUINTA',
  'SEXTA',
  'SABADO',
];

export function parseHm(hm: string): number {
  const [h, m] = hm.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

export function dateToHm(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function dateToMinutes(d: Date): number {
  return d.getHours() * 60 + d.getMinutes();
}

export function setDateToHm(d: Date, hm: string): Date {
  const next = new Date(d);
  const [h, m, s] = hm.split(':').map(Number);
  next.setHours(h || 0, m || 0, Number.isFinite(s) ? s : 0, 0);
  return next;
}

/** Relógio virtual ancorado na data simulada (não na data civil do wall clock). */
export function dateAtYmdHm(ymd: string, hm: string): Date {
  return setDateToHm(parseYmd(ymd), hm);
}

/**
 * Janelas reais de portaria (entrada cedo / saída fim do turno).
 * Usado para carimbar RFID no dia simulado — nunca “tudo ao meio-dia”.
 */
export const RFID_FLOW_WINDOWS: Record<
  ShiftKey,
  { ENTRADA: [string, string]; SAIDA: [string, string] }
> = {
  MANHA: { ENTRADA: ['06:40', '08:00'], SAIDA: ['11:00', '12:20'] },
  TARDE: { ENTRADA: ['12:40', '14:00'], SAIDA: ['17:00', '18:20'] },
  NOITE: { ENTRADA: ['18:10', '19:30'], SAIDA: ['21:30', '22:30'] },
};

/** % padrão de entradas após o limite de atraso (war-room). */
export const DEFAULT_RFID_PCT_ATRASADOS = 22;

/**
 * Mesma regra do war-room: abertura 07:00 → limite 07:00;
 * atrasado se h > limite || (h === limite && m > 0).
 * Retorna minutos desde meia-noite do último instante pontual (limite:00).
 */
export function warRoomLimiteAtrasoMin(aberturaHm: string): number {
  const total = parseHm(aberturaHm);
  const abH = Math.floor(total / 60);
  const abM = total % 60;
  const horaLimite = abM >= 30 ? abH + 1 : abH;
  return horaLimite * 60;
}

function minutesToHm(mins: number): string {
  const clamped = Math.max(0, Math.min(23 * 60 + 59, Math.round(mins)));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function isoAtYmdMinutes(
  ymd: string,
  mins: number,
  sec = 0,
): string {
  const [yy, mm, dd] = ymd.split('-').map(Number);
  const clamped = Math.max(0, Math.min(23 * 60 + 59, Math.round(mins)));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return new Date(yy, (mm || 1) - 1, dd || 1, h, m, sec % 60, 0).toISOString();
}

function spreadMinutesInWindow(
  count: number,
  startMin: number,
  endMin: number,
  bias: 'early' | 'late',
): number[] {
  if (count <= 0) return [];
  const span = Math.max(1, endMin - startMin);
  const out: number[] = [];
  for (let i = 0; i < count; i++) {
    const t = count <= 1 ? 0.35 : i / (count - 1);
    const biased = bias === 'early' ? Math.pow(t, 1.4) : 1 - Math.pow(1 - t, 1.35);
    const jitter = ((i * 17) % 7) - 3;
    out.push(Math.max(startMin, Math.min(endMin, Math.round(startMin + biased * span + jitter))));
  }
  return out;
}

export type RfidIsoBatch = {
  isoList: string[];
  pontuais: number;
  atrasados: number;
  firstHm: string;
  lastHm: string;
  limiteHm: string;
  aberturaHm: string;
  saidaHm: string;
};

/**
 * Gera ISO timestamps no dia simulado.
 * ENTRADA: coorte pontual + ~pctAtrasados após limite (regra war-room).
 * SAÍDA: curva perto de saidaHm / janela padrão.
 */
export function buildRfidEventIsoList(opts: {
  simDateYmd: string;
  shift: ShiftKey;
  tipoEvento: 'ENTRADA' | 'SAIDA';
  count: number;
  atHm?: string;
  virtualTime?: Date;
  /** boundaries.abertura do dayPlan */
  aberturaHm?: string;
  /** boundaries.entrada do dayPlan */
  entradaHm?: string;
  /** boundaries.saida do dayPlan */
  saidaHm?: string;
  /** 0–100; default DEFAULT_RFID_PCT_ATRASADOS */
  pctAtrasados?: number;
}): RfidIsoBatch {
  const win = RFID_FLOW_WINDOWS[opts.shift][opts.tipoEvento];
  const aberturaHm = opts.aberturaHm || SHIFT_WINDOWS[opts.shift].startHm || win[0];
  const entradaHm = opts.entradaHm || opts.atHm || win[0];
  const saidaHm = opts.saidaHm || (opts.tipoEvento === 'SAIDA' ? opts.atHm : undefined) || win[0];
  const limiteMin = warRoomLimiteAtrasoMin(aberturaHm);
  const limiteHm = minutesToHm(limiteMin);

  let startMin = parseHm(win[0]);
  let endMin = parseHm(win[1]);

  if (opts.tipoEvento === 'SAIDA') {
    const center = parseHm(opts.atHm || saidaHm);
    startMin = Math.max(0, center - 40);
    endMin = Math.min(23 * 60 + 50, center + 25);
    // Preferir janela padrão do turno se boundaries.saida cair fora
    const winStart = parseHm(win[0]);
    const winEnd = parseHm(win[1]);
    if (center < winStart - 30 || center > winEnd + 30) {
      startMin = winStart;
      endMin = winEnd;
    }
  } else {
    // ENTRADA: da abertura até fim da janela de chegada
    startMin = parseHm(aberturaHm);
    endMin = parseHm(win[1]);
    if (opts.atHm || opts.entradaHm) {
      const peak = parseHm(opts.atHm || entradaHm);
      startMin = Math.min(startMin, Math.max(0, peak - 25));
      endMin = Math.max(endMin, Math.min(23 * 60 + 50, peak + 55));
    }
    // Garante que atraso caiba depois do limite
    if (endMin <= limiteMin + 5) {
      endMin = Math.min(23 * 60 + 50, limiteMin + 55);
    }
  }

  if (
    opts.tipoEvento === 'ENTRADA' &&
    opts.virtualTime &&
    formatYmd(opts.virtualTime) === opts.simDateYmd &&
    !opts.atHm
  ) {
    const cur = dateToMinutes(opts.virtualTime);
    if (cur >= startMin - 45 && cur <= endMin + 45) {
      // Âncora leve sem esmagar a coorte de atrasados
      startMin = Math.min(startMin, Math.max(parseHm(aberturaHm), cur - 15));
    }
  }

  const empty: RfidIsoBatch = {
    isoList: [],
    pontuais: 0,
    atrasados: 0,
    firstHm: '—',
    lastHm: '—',
    limiteHm,
    aberturaHm,
    saidaHm,
  };
  if (opts.count <= 0) return empty;

  const minutes: number[] = [];
  let pontuais = 0;
  let atrasados = 0;

  if (opts.tipoEvento === 'SAIDA') {
    minutes.push(...spreadMinutesInWindow(opts.count, startMin, endMin, 'late'));
  } else {
    const pct = Math.max(
      0,
      Math.min(100, opts.pctAtrasados ?? DEFAULT_RFID_PCT_ATRASADOS),
    );
    atrasados =
      opts.count <= 1
        ? pct >= 50
          ? 1
          : 0
        : Math.max(0, Math.min(opts.count - 1, Math.round((opts.count * pct) / 100)));
    pontuais = opts.count - atrasados;

    const pontualEnd = limiteMin; // inclusive XX:00
    const pontualStart = Math.min(startMin, pontualEnd);
    // Pontuais: abertura → limite:00 (pico cedo)
    const pStart = Math.min(pontualStart, pontualEnd);
    const pEnd = Math.max(pStart, pontualEnd);
    minutes.push(...spreadMinutesInWindow(pontuais, pStart, pEnd, 'early'));
    // Forçar pontuais com m===0 na hora limite ou antes (nunca m>0 na hora limite)
    for (let i = 0; i < pontuais; i++) {
      if (minutes[i] > pontualEnd) minutes[i] = pontualEnd;
      if (minutes[i] === pontualEnd) {
        /* ok: XX:00 */
      } else if (Math.floor(minutes[i] / 60) === Math.floor(pontualEnd / 60) && minutes[i] % 60 > 0) {
        minutes[i] = pontualEnd; // evita falso atraso na hora limite
      }
    }

    const lateStart = limiteMin + 1; // XX:01
    const lateEnd = Math.max(lateStart + 10, endMin);
    minutes.push(...spreadMinutesInWindow(atrasados, lateStart, lateEnd, 'early'));
  }

  const isoList = minutes.map((mins, i) => isoAtYmdMinutes(opts.simDateYmd, mins, (i * 13) % 60));
  if (opts.tipoEvento === 'ENTRADA') {
    pontuais = minutes.filter((m) => m <= limiteMin).length;
    atrasados = minutes.filter((m) => m > limiteMin).length;
  }

  const sorted = [...minutes].sort((a, b) => a - b);
  return {
    isoList,
    pontuais,
    atrasados,
    firstHm: sorted.length ? minutesToHm(sorted[0]) : '—',
    lastHm: sorted.length ? minutesToHm(sorted[sorted.length - 1]) : '—',
    limiteHm,
    aberturaHm,
    saidaHm,
  };
}

export function formatYmd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function parseYmd(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1, 12, 0, 0, 0);
}

export function ymdToDiaSemana(ymd: string): DiaSemanaKey {
  const d = parseYmd(ymd);
  const js = d.getDay(); // 0=Dom
  if (js === 0) return 'SEGUNDA';
  return JS_TO_DIA[js] || 'SEGUNDA';
}

/** Próximo dia letivo: hoje se SEG–SEX; senão próxima segunda. */
export function defaultSimSchoolDay(): { ymd: string; diaSemana: DiaSemanaKey } {
  const now = new Date();
  const js = now.getDay();
  if (js >= 1 && js <= 5) {
    const ymd = formatYmd(now);
    return { ymd, diaSemana: ymdToDiaSemana(ymd) };
  }
  // Sábado (6) → +2; Domingo (0) → +1
  const add = js === 6 ? 2 : 1;
  const next = new Date(now);
  next.setDate(now.getDate() + add);
  const ymd = formatYmd(next);
  return { ymd, diaSemana: 'SEGUNDA' };
}

/** Ajusta ymd para cair no diaSemana desejado (próxima ocorrência a partir de base). */
export function ymdForDiaSemana(dia: DiaSemanaKey, fromYmd?: string): string {
  const base = fromYmd ? parseYmd(fromYmd) : new Date();
  const targetJs =
    dia === 'SEGUNDA' ? 1 :
    dia === 'TERCA' ? 2 :
    dia === 'QUARTA' ? 3 :
    dia === 'QUINTA' ? 4 :
    dia === 'SEXTA' ? 5 : 6;
  const cur = new Date(base);
  for (let i = 0; i < 8; i++) {
    if (cur.getDay() === targetJs) return formatYmd(cur);
    cur.setDate(cur.getDate() + 1);
  }
  return formatYmd(base);
}

export function formatSimDateBadge(ymd: string, dia: DiaSemanaKey): string {
  const d = parseYmd(ymd);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${DIA_LABEL[dia]} ${dd}/${mm}`;
}

export function buildQueueForShift(shift: ShiftKey): SimEvent[] {
  const win = SHIFT_WINDOWS[shift];
  const saidaAt = RFID_FLOW_WINDOWS[shift].SAIDA[0];
  const events: SimEvent[] = win.events.map((e, i) => ({
    id: `${shift}-${e.kind}-${i}`,
    at: e.at,
    kind: e.kind,
    label: e.label,
    status: 'pending' as const,
  }));
  events.push({
    id: `${shift}-rfid-saida`,
    at: saidaAt,
    kind: 'rfid_saida',
    label: 'Saída RFID',
    status: 'pending',
  });
  events.sort((a, b) => parseHm(a.at) - parseHm(b.at));
  return events;
}

/** Fila completa a partir do plano do turno (entrada → aulas/recreio → saída → comunicado). */
export function buildQueueFromPlan(plan: ShiftDayPlan): SimEvent[] {
  if (!plan?.success || !plan.boundaries) return [];
  const b = plan.boundaries;
  const events: SimEvent[] = [];

  events.push({
    id: 'rfid-entrada',
    at: b.entrada || b.abertura,
    kind: 'rfid',
    label: 'Entrada RFID',
    status: 'pending',
  });

  let firstAulaDone = false;
  const seenRecreio = new Set<string>();
  for (const slot of plan.slots || []) {
    if (slot.tipo === 'INTERVALO') {
      const rKey = `${slot.at}-${slot.end}`;
      if (seenRecreio.has(rKey)) continue;
      seenRecreio.add(rKey);
      events.push({
        id: `recreio-${slot.at}`,
        at: slot.at,
        end: slot.end,
        kind: 'recreio',
        label: slot.sintetico ? 'Recreio (gap)' : slot.label || 'Recreio',
        status: 'pending',
      });
      continue;
    }
    if (slot.tipo === 'AULA') {
      const lista = slot.chamadas?.length
        ? slot.chamadas
        : [null];
      // Uma entrada na fila por turma/professor do mesmo horário
      for (let i = 0; i < lista.length; i++) {
        const ch = lista[i];
        const turmaKey = ch?.turmaId || `slot-${slot.at}-${i}`;
        const num = slot.numeroAula ?? undefined;
        events.push({
          id: `chamada-${num ?? slot.at}-${turmaKey}`,
          at: slot.at,
          end: slot.end,
          kind: 'chamada',
          label: ch
            ? `Chamada · ${slot.label} · ${ch.turmaNome}${ch.professorNome ? ` · ${ch.professorNome}` : ''}`
            : `Chamada · ${slot.label}`,
          status: 'pending',
          turmaId: ch?.turmaId,
          turmaNome: ch?.turmaNome,
          professorId: ch?.professorId || undefined,
          professorNome: ch?.professorNome || undefined,
          materiaId: ch?.materiaId || undefined,
          horarioAulaId: ch?.horarioAulaId || undefined,
          numeroAula: num,
        });
      }
      if (!firstAulaDone) {
        firstAulaDone = true;
        // Comunicado logo após início da 1ª aula (não no recreio)
        const afterFirst = parseHm(slot.at) + 5;
        const h = Math.floor(afterFirst / 60);
        const m = afterFirst % 60;
        events.push({
          id: 'comunicado-pos-1aula',
          at: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`,
          kind: 'comunicado',
          label: 'Comunicado municipal',
          status: 'pending',
        });
      }
    }
  }

  events.push({
    id: 'rfid-saida',
    at: b.saida,
    kind: 'rfid_saida',
    label: 'Saída RFID',
    status: 'pending',
  });

  events.sort((a, b) => parseHm(a.at) - parseHm(b.at));
  return events;
}

export function matchTurnoToShift(nome: string): ShiftKey | null {
  const n = String(nome || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();
  if (n.includes('MANHA') || n.includes('MATUT')) return 'MANHA';
  if (n.includes('TARDE') || n.includes('VESP')) return 'TARDE';
  if (n.includes('NOITE') || n.includes('NOTUR')) return 'NOITE';
  return null;
}

/** Progresso 0–1 usando boundaries do plano ou fallback do turno. */
export function timelineProgress(
  virtualTime: Date,
  startHm: string,
  endHm: string,
): number {
  const start = parseHm(startHm);
  const end = parseHm(endHm);
  const cur = dateToMinutes(virtualTime);
  if (end <= start) return 0;
  return Math.min(1, Math.max(0, (cur - start) / (end - start)));
}

export function shiftProgress(virtualTime: Date, shift: ShiftKey): number {
  const win = SHIFT_WINDOWS[shift];
  return timelineProgress(virtualTime, win.startHm, win.endHm);
}

export function eventMarkerPercent(
  at: string,
  startHm: string,
  endHm: string,
): number {
  const start = parseHm(startHm);
  const end = parseHm(endHm);
  const t = parseHm(at);
  if (end <= start) return 0;
  return Math.min(1, Math.max(0, (t - start) / (end - start))) * 100;
}
